import { XMarkIcon } from "@heroicons/react/20/solid";
import SelectComponent from "@via/components/select/select";
import {
  type FilterCriteria,
  type FilterFieldOption,
  FILTER_FIELD_OPTIONS,
} from "@via/hooks/runs-filter-types.ts";
import SchedulerValueInput from "@via/pages/runs/components/scheduler-value-input";
import StatusValueInput from "@via/pages/runs/components/status-value-input";
import TagValueInput from "@via/components/tag-value-input";
import { type SelectOption, isSelectOption } from "@via/utils/interfaces";
import { TaggableEntityType } from "common";

interface FilterCriteriaRowProps {
  criteria: FilterCriteria;
  fieldOptions: FilterFieldOption[];
  onFieldChange: (field: string) => void;
  onValueChange: (value: string) => void;
  onRemove: () => void;
}

const fieldSelectOptions: SelectOption[] = FILTER_FIELD_OPTIONS.map(opt => ({
  label: opt.label,
  value: opt.value,
}));

export default function FilterCriteriaRow({
  criteria,
  fieldOptions,
  onFieldChange,
  onValueChange,
  onRemove,
}: FilterCriteriaRowProps) {
  const selectedFieldConfig = fieldOptions.find(
    f => f.value === criteria.field
  );

  const renderValueInput = () => {
    if (
      selectedFieldConfig?.inputType === "select" ||
      selectedFieldConfig?.inputType === "multi-select"
    ) {
      if (criteria.field === "schedulerId") {
        return (
          <SchedulerValueInput
            value={criteria.value}
            onChange={onValueChange}
          />
        );
      }
      if (criteria.field === "tag") {
        return (
          <TagValueInput
            entityType={TaggableEntityType.Run}
            value={criteria.value}
            onChange={onValueChange}
          />
        );
      }
      return (
        <StatusValueInput value={criteria.value} onChange={onValueChange} />
      );
    }
    return (
      <input
        type="text"
        value={criteria.value}
        onChange={e => onValueChange(e.target.value)}
        placeholder="Enter value..."
        className="w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
      />
    );
  };

  return (
    <div className="flex items-center gap-2">
      <div className="w-48 shrink-0">
        <SelectComponent
          name={`field-${criteria.id}`}
          options={fieldSelectOptions}
          value={
            fieldSelectOptions.find(opt => opt.value === criteria.field) ?? null
          }
          onChange={selected => {
            if (isSelectOption(selected)) {
              onFieldChange(selected.value as string);
            }
          }}
          placeholder="Select field"
          isSearchable={false}
        />
      </div>

      <div className="flex-1 min-w-0">{renderValueInput()}</div>

      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 rounded p-1 text-gray-400 hover:text-red-500 transition-colors"
        aria-label="Remove filter">
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
